import React, { useId, useMemo, useState } from 'react';
import type { WarRoomConsensus, EngineConsensusEntry } from '../../types/warRoom';

export interface ConsensusViewProps {
  consensus?: WarRoomConsensus | null;
  isLoading?: boolean;
  error?: unknown;
  defaultExpanded?: boolean;
  engineDisplayNames?: Record<string, string>;
  className?: string;
}

function humanize(id: string): string {
  return id
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isRedacted(s?: string): boolean {
  return !!s && s.startsWith('[REDACTED');
}

function fmtTs(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi} UTC`;
}

function fmtConfidence(c?: number): string {
  if (c === undefined || c === null) return '—';
  const pct = c <= 1 ? Math.round(c * 100) : Math.round(c);
  return `${pct}%`;
}

function isCurrencyKey(key: string): boolean {
  return /(_value|_amount|_loss|_claim|_settlement|_damages|_fee|_cost|_recovery|net_to_|gross_|net_claim)/i.test(key);
}

function isIntegerKey(key: string): boolean {
  return /(_months|_days|_count|_events)/i.test(key);
}

function fmtField(
  v: number | string | null | undefined,
  key: string
): React.ReactNode {
  if (v === null || v === undefined) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }
  if (typeof v === 'string') return v;
  if (typeof v === 'number') {
    const isCurrency = isCurrencyKey(key);
    if (v === 0 && isCurrency) {
      return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
    }
    const absVal = Math.abs(v);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isCurrency ? 2 : 0,
      maximumFractionDigits: isCurrency ? 2 : 0,
    }).format(absVal);
    const prefix = isCurrency ? '$' : '';
    const content = `${prefix}${formatted}`;
    if (v < 0) {
      return (
        <span style={{ color: 'var(--color-blocker)' }}>({content})</span>
      );
    }
    return content;
  }
  return String(v);
}

function fmtDelta(value: number, key: string): string {
  const isCurrency = isCurrencyKey(key);
  const absVal = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: isCurrency ? 2 : 0,
    maximumFractionDigits: isCurrency ? 2 : 0,
  }).format(absVal);
  const prefix = isCurrency ? '$' : '';
  const sign = value >= 0 ? '+' : '-';
  return `${sign}${prefix}${formatted}`;
}

export default function ConsensusView({
  consensus,
  isLoading,
  error,
  defaultExpanded = false,
  engineDisplayNames,
  className,
}: ConsensusViewProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [expandedEngines, setExpandedEngines] = useState<Set<string>>(new Set());
  const detailId = useId();
  const baseId = useId();

  const toggleEngine = (engineId: string) => {
    setExpandedEngines((prev) => {
      const next = new Set(prev);
      if (next.has(engineId)) next.delete(engineId);
      else next.add(engineId);
      return next;
    });
  };

  const sortedEngines = useMemo(() => {
    const entries = Object.entries(consensus?.agreed_inputs || {}) as Array<[string, EngineConsensusEntry]>;
    return entries.sort((a, b) => {
      const aDissents = a[1].dissents?.length || 0;
      const bDissents = b[1].dissents?.length || 0;
      if (aDissents > 0 && bDissents === 0) return -1;
      if (bDissents > 0 && aDissents === 0) return 1;
      const aName = engineDisplayNames?.[a[0]] || humanize(a[0]);
      const bName = engineDisplayNames?.[b[0]] || humanize(b[0]);
      return aName.localeCompare(bName);
    });
  }, [consensus?.agreed_inputs, engineDisplayNames]);

  const summary = consensus?.summary;
  const unresolved = consensus?.unresolved_disputes || [];

  const Chevron = ({ open }: { open: boolean }) => (
    <div className="w-8 h-8 flex items-center justify-center">
      <svg
        className={`w-4 h-4 motion-safe:transition-transform motion-safe:duration-150 ${open ? 'rotate-180' : ''}`}
        style={{ color: open ? 'var(--color-brand)' : 'var(--color-text-muted)' }}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );

  const UsersIcon = () => (
    <svg
      className="w-5 h-5"
      style={{ color: 'var(--color-needs-human-text)' }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );

  if (isLoading) {
    return (
      <div
        className={`rounded-lg border p-4 ${className || ''}`}
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
        aria-busy="true"
        role="status"
        data-testid="consensus-view-loading"
      >
        <span className="sr-only">Loading consensus…</span>
        <div className="flex items-center gap-2">
          <div className="h-4 w-44 rounded" style={{ backgroundColor: 'var(--color-bg-surface)' }} />
        </div>
        <div
          className="mt-3 grid grid-cols-4 rounded-md border overflow-hidden"
          style={{ borderColor: 'var(--color-border)' }}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 border-l first:border-l-0"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            />
          ))}
        </div>
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 rounded border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`rounded-lg border p-4 ${className || ''}`}
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
        data-testid="consensus-view-error"
      >
        <div className="flex items-center gap-2">
          <UsersIcon />
          <span 
            className="font-serif font-semibold"
            style={{
              color: 'var(--color-text-primary)',
              fontSize: '22px'
            }}
          >
            War Room Consensus
          </span>
        </div>
        <div className="mt-3">
          <span className="text-sm font-medium" style={{ color: 'var(--color-blocker)' }}>
            Consensus unavailable.
          </span>{' '}
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            The war-room record could not be loaded.
          </span>
        </div>
      </div>
    );
  }

  if (!consensus) {
    return (
      <div
        className={`rounded-lg border p-4 ${className || ''}`}
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
        data-testid="consensus-view-empty"
      >
        <div className="flex items-center gap-2">
          <UsersIcon />
          <span 
            className="font-serif font-semibold"
            style={{
              color: 'var(--color-text-primary)',
              fontSize: '22px'
            }}
          >
            War Room Consensus
          </span>
        </div>
        <div className="py-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No consensus recorded yet for this case.
        </div>
      </div>
    );
  }

  const fullConsensusCount = summary?.engines_with_full_consensus ?? 0;
  const withDissentCount = summary?.engines_with_dissent ?? 0;
  const blockingCount = summary?.blocking_disputes ?? 0;
  const participation = summary?.agent_participation;
  let participationDisplay = '—';
  if (typeof participation === 'number') {
    participationDisplay = participation <= 1 ? `${Math.round(participation * 100)}%` : String(participation);
  } else if (participation && typeof participation === 'object') {
    participationDisplay = String(Object.keys(participation).length);
  }

  const runIdFull = consensus.run_id || '—';
  const runIdShort = runIdFull.length > 16 ? `${runIdFull.slice(0, 8)}…${runIdFull.slice(-4)}` : runIdFull;
  const provenanceTitle = `run ${runIdFull} · produced ${consensus.produced_at || '—'}`;

  return (
    <div
      className={`rounded-lg border p-4 ${className || ''}`}
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
      data-testid="consensus-view"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-[var(--color-brand)]"
        aria-expanded={expanded}
        aria-controls={detailId}
      >
        <div className="flex items-center gap-2">
          <UsersIcon />
          <span 
            className="font-serif font-semibold"
            style={{
              color: 'var(--color-text-primary)',
              fontSize: '22px'
            }}
          >
            War Room Consensus
          </span>
        </div>
        <Chevron open={expanded} />
      </button>

      <div
        className="mt-1 text-xs font-mono tabular-nums truncate"
        style={{ color: 'var(--color-text-muted)' }}
        title={provenanceTitle}
      >
        run {runIdShort} · produced {fmtTs(consensus.produced_at)}
      </div>

      <div
        className="mt-3 grid grid-cols-4 rounded-md border overflow-hidden"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}
        data-testid="consensus-view-summary"
      >
        {[
          { label: 'Full consensus', value: fullConsensusCount, dot: null as 'active' | 'blocker' | null },
          { label: 'With dissent', value: withDissentCount, dot: 'active' as const },
          { label: 'Blocking', value: blockingCount, dot: 'blocker' as const },
          { label: 'Agents', value: participationDisplay, dot: null },
        ].map((cell) => {
          const dotColor = cell.dot === 'active' ? 'var(--color-brand)' : cell.dot === 'blocker' ? 'var(--color-blocker)' : null;
          return (
            <div
              key={cell.label}
              className="px-3 py-2 border-l first:border-l-0"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <span
                className="block text-[10px] font-medium uppercase tracking-wider truncate"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {cell.label}
              </span>
              <span
                className="block mt-0.5 font-mono tabular-nums leading-tight flex items-center gap-1.5"
                style={{ color: 'var(--color-text-primary)', fontSize: '20px', lineHeight: '24px' }}
              >
                {dotColor && (
                  <span
                    className="inline-block rounded-full"
                    style={{ width: '6px', height: '6px', backgroundColor: dotColor, flexShrink: 0 }}
                  />
                )}
                {cell.value}
              </span>
            </div>
          );
        })}
      </div>

      {expanded && (
        <div id={detailId} className="mt-3 space-y-2">
          {sortedEngines.map(([engineId, engine], idx) => {
            const displayName = engineDisplayNames?.[engineId] || humanize(engineId);
            const engineOpen = expandedEngines.has(engineId);
            const engineDetailId = `${baseId}-eng-${idx}`;
            const inputEntries = Object.entries(engine.inputs || {});
            const topInputs = inputEntries.slice(0, 2);
            const moreCount = inputEntries.length - 2;
            const hasDissents = (engine.dissents?.length || 0) > 0;
            const dissentedFields = new Set(engine.dissents?.map(d => d.field_id).filter(Boolean) || []);

            const allSources = inputEntries.map(([, v]) => v.source).filter((s): s is string => !!s);
            const uniqueSources = [...new Set(allSources)];
            const singleSource = uniqueSources.length === 1 ? uniqueSources[0] : null;

            return (
              <div
                key={engineId}
                className="rounded border overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                data-testid={`consensus-engine-${engineId}`}
              >
                <button
                  type="button"
                  onClick={() => toggleEngine(engineId)}
                  className="w-full px-3 py-2 text-left flex items-start gap-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-[var(--color-brand)]"
                  style={{ backgroundColor: engineOpen ? 'var(--color-bg-elevated)' : 'var(--color-bg-surface)' }}
                  aria-expanded={engineOpen}
                  aria-controls={engineDetailId}
                >
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div 
                      className="font-medium truncate" 
                      style={{ 
                        color: 'var(--color-text-primary)', 
                        fontSize: '15px', 
                        fontWeight: 500, 
                        lineHeight: '22px' 
                      }}
                    >
                      {displayName}
                    </div>
                    <div className="mt-0.5 text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {engine.method?.name ? (
                        <>
                          {engine.method.name} ·{' '}
                          <span className="font-mono tabular-nums">{fmtConfidence(engine.method.confidence)}</span>
                        </>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                  <div 
                    className="shrink-0" 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'auto minmax(10ch, auto)', 
                      columnGap: '12px', 
                      rowGap: '2px', 
                      justifyContent: 'end' 
                    }}
                  >
                    {topInputs.map(([fieldName, inputVal]) => {
                      const isDissented = dissentedFields.has(fieldName);
                      return (
                        <React.Fragment key={fieldName}>
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)', textAlign: 'right' }}>
                            {fieldName}
                            {isDissented && (
                              <span
                                className="inline-block rounded-full ml-1"
                                style={{ width: '6px', height: '6px', backgroundColor: 'var(--color-brand)' }}
                              />
                            )}
                          </span>
                          <span
                            className="text-sm font-mono tabular-nums"
                            style={{ color: 'var(--color-text-primary)', textAlign: 'right' }}
                          >
                            {fmtField(inputVal.value, fieldName)}
                            {fieldName === 'score' && typeof inputVal.value === 'number' ? ' / 100' : ''}
                          </span>
                        </React.Fragment>
                      );
                    })}
                    {moreCount > 0 && (
                      <>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)', textAlign: 'right' }}></span>
                        <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)', textAlign: 'right' }}>
                          +{moreCount} more
                        </span>
                      </>
                    )}
                    {hasDissents && (
                      <>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)', textAlign: 'right' }}></span>
                        <div
                          className="text-xs font-medium flex items-center justify-end gap-1"
                          style={{ color: 'var(--color-needs-human-text)' }}
                          data-testid={`consensus-engine-dissent-${engineId}`}
                        >
                          <span
                            className="inline-block rounded-full"
                            style={{ width: '6px', height: '6px', backgroundColor: 'var(--color-brand)' }}
                          />
                          {engine.dissents!.length} dissent{engine.dissents!.length > 1 ? 's' : ''}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="ml-2 shrink-0">
                    <Chevron open={engineOpen} />
                  </div>
                </button>

                {engineOpen && (
                  <div
                    id={engineDetailId}
                    aria-label={displayName}
                    className="border-t px-3 pb-3 space-y-3"
                    style={{ borderColor: 'var(--color-border)', borderTopWidth: '1px', backgroundColor: 'var(--color-bg-elevated)' }}
                  >
                    {singleSource && (
                      <div className="text-right mt-2" style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}>
                        SOURCE {singleSource}
                      </div>
                    )}
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: '24px', rowGap: '8px' }}>
                      {inputEntries.map(([fieldName, inputVal]) => (
                        <React.Fragment key={fieldName}>
                          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {fieldName}
                          </span>
                          <div className="text-right">
                            <span
                              className="text-sm font-mono tabular-nums"
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {fmtField(inputVal.value, fieldName)}
                              {fieldName === 'score' && typeof inputVal.value === 'number' ? ' / 100' : ''}
                            </span>
                            {!singleSource && inputVal.source && (
                              <span
                                className="block text-xs truncate max-w-32"
                                style={{ 
                                  color: 'var(--color-text-muted)', 
                                  direction: 'rtl', 
                                  textAlign: 'left',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={inputVal.source}
                              >
                                {inputVal.source}
                              </span>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>

                    {engine.method?.rationale && !isRedacted(engine.method.rationale) && (
                      <p 
                        className="text-sm" 
                        style={{ 
                          color: 'var(--color-text-secondary)', 
                          lineHeight: '1.55', 
                          maxWidth: '60ch' 
                        }}
                      >
                        {engine.method.rationale}
                      </p>
                    )}
                    {engine.method?.rationale && isRedacted(engine.method.rationale) && (
                      <p
                        className="text-xs italic"
                        style={{ color: 'var(--color-text-muted)' }}
                        data-testid="rationale-redacted"
                      >
                        — rationale restricted —
                      </p>
                    )}

                    {hasDissents && (
                      <div
                        className="border-l-2 pl-3 py-1 space-y-3"
                        style={{ borderLeftColor: 'var(--color-brand)' }}
                      >
                        <div
                          className="font-semibold uppercase"
                          style={{ 
                            color: 'var(--color-needs-human-text)', 
                            fontSize: '10px', 
                            letterSpacing: '0.08em' 
                          }}
                        >
                          Dissent
                        </div>
                        {engine.dissents!.map((d, i) => {
                          const agreedValue = typeof d.field_id === 'string' ? engine.inputs?.[d.field_id]?.value : undefined;
                          const altValue = d.alternative_value;
                          const showDelta = typeof agreedValue === 'number' && typeof altValue === 'number';
                          const delta = showDelta ? altValue - agreedValue : 0;
                          const pct = showDelta && agreedValue !== 0 ? (delta / agreedValue) * 100 : 0;
                          
                          return (
                            <div key={i}>
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="text-xs">
                                  <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                                    {d.agent_name || 'Unknown'}
                                  </span>
                                  <span style={{ color: 'var(--color-text-muted)' }}>
                                    {d.field_id ? ` on ${d.field_id}` : ''}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span
                                    className="text-sm font-mono tabular-nums block"
                                    style={{ color: 'var(--color-text-primary)' }}
                                  >
                                    {fmtField(d.alternative_value, d.field_id || '')}
                                  </span>
                                  {showDelta && (
                                    <span 
                                      className="block font-mono" 
                                      style={{ color: 'var(--color-text-muted)', fontSize: '11px' }}
                                    >
                                      Δ {fmtDelta(delta, d.field_id || '')} · {delta >= 0 ? '+' : ''}{pct.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              {d.resolved_by && (
                                <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                                  consensus method · {d.resolved_by}
                                </div>
                              )}
                              {d.rationale && !isRedacted(d.rationale) && (
                                <p 
                                  className="text-sm mt-2" 
                                  style={{ 
                                    color: 'var(--color-text-secondary)', 
                                    lineHeight: '1.55', 
                                    maxWidth: '60ch' 
                                  }}
                                >
                                  {d.rationale}
                                </p>
                              )}
                              {d.rationale && isRedacted(d.rationale) && (
                                <p
                                  className="text-xs italic mt-2"
                                  style={{ color: 'var(--color-text-muted)' }}
                                  data-testid="rationale-redacted"
                                >
                                  — rationale restricted —
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div
            className={`mt-3 rounded-md border px-3 py-2 ${unresolved.length > 0 ? 'border-l-2' : ''}`}
            style={{
              borderColor: 'var(--color-border)',
              borderLeftColor: unresolved.length > 0 ? 'var(--color-blocker)' : undefined,
              backgroundColor: 'var(--color-bg-surface)',
            }}
            data-testid={unresolved.length > 0 ? 'consensus-disputes' : 'consensus-disputes-empty'}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: unresolved.length > 0 ? 'var(--color-blocker)' : 'var(--color-text-muted)' }}
            >
              Unresolved disputes ({unresolved.length})
            </div>
            {unresolved.length > 0 ? (
              <div className="mt-2 space-y-2">
                {unresolved.map((u, i) => (
                  <p key={i} className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                    {u.description}
                  </p>
                ))}
              </div>
            ) : (
              <div className="mt-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                No unresolved disputes.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
